/**
 * SearchSelect — a searchable single-select combobox for filter bars.
 *
 * Built for lists that can reach thousands of rows (agents in production):
 * type-to-filter over the loaded options, plus an optional `onSearch` hook
 * that lets the parent fetch server-side matches (debounced here) when the
 * loaded list is only a slice of the full roster.
 *
 * Keyboard: ArrowUp/Down to move, Enter to pick, Escape to close.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Small gray text under the label (e.g. org name, id). */
  sublabel?: string;
  /** Right-aligned text (e.g. cost). */
  right?: string;
  /** Tiny colored badge next to the label (e.g. APP / NAV). */
  badge?: { text: string; className: string };
}

interface SearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  /** Shown when value === '' (the "All …" row is prepended automatically). */
  allLabel: string;
  placeholder?: string;
  /** Debounced server-side search. Return value is ignored — the parent is
   * expected to merge results into `options`. */
  onSearch?: (q: string) => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  /** Footer hint, e.g. "showing top 200 — type to search all". */
  footnote?: string;
}

export const SearchSelect: React.FC<SearchSelectProps> = ({
  value, onChange, options, allLabel, placeholder = 'Search…',
  onSearch, loading = false, disabled = false, className = '', footnote,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  // Rows rendered = "All" pseudo-row + filtered options.
  const rows: (SearchSelectOption | null)[] = useMemo(
    () => [null, ...filtered],
    [filtered]
  );

  useEffect(() => { setHighlight(0); }, [query, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const fireSearch = useCallback((q: string) => {
    if (!onSearch) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearch(q), 300);
  }, [onSearch]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[highlight];
      pick(row ? row.value : '');
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    const el = listRef.current?.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((o) => !o);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="w-full flex items-center justify-between gap-2 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-left hover:border-gray-400 disabled:opacity-50"
      >
        <span className="flex items-center gap-1.5 min-w-0">
          {selected ? (
            <>
              {selected.badge && (
                <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded border ${selected.badge.className}`}>
                  {selected.badge.text}
                </span>
              )}
              <span className="truncate text-gray-900">{selected.label}</span>
            </>
          ) : (
            <span className="text-gray-600">{allLabel}</span>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && !disabled && (
            <X
              className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700"
              onClick={(e) => { e.stopPropagation(); pick(''); }}
            />
          )}
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </span>
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[260px] rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            {loading
              ? <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin shrink-0" />
              : <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); fireSearch(e.target.value); }}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="w-full border-0 p-0 text-sm focus:ring-0 placeholder:text-gray-400"
            />
          </div>
          <div ref={listRef} className="max-h-72 overflow-y-auto py-1">
            {/* "All" row */}
            <div
              onClick={() => pick('')}
              onMouseEnter={() => setHighlight(0)}
              className={`px-3 py-1.5 text-sm cursor-pointer ${highlight === 0 ? 'bg-blue-50' : ''} ${!value ? 'font-medium text-blue-700' : 'text-gray-700'}`}
            >
              {allLabel}
            </div>
            {filtered.map((o, i) => {
              const idx = i + 1;
              return (
                <div
                  key={o.value}
                  onClick={() => pick(o.value)}
                  onMouseEnter={() => setHighlight(idx)}
                  className={`flex items-center justify-between gap-2 px-3 py-1.5 cursor-pointer ${highlight === idx ? 'bg-blue-50' : ''}`}
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    {o.badge && (
                      <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded border ${o.badge.className}`}>
                        {o.badge.text}
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className={`block truncate text-sm ${o.value === value ? 'font-medium text-blue-700' : 'text-gray-900'}`}>
                        {o.label}
                      </span>
                      {o.sublabel && (
                        <span className="block truncate text-[11px] text-gray-500">{o.sublabel}</span>
                      )}
                    </span>
                  </span>
                  {o.right && (
                    <span className="shrink-0 text-xs tabular-nums text-gray-500">{o.right}</span>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                {loading ? 'Searching…' : 'No matches.'}
              </div>
            )}
          </div>
          {footnote && (
            <div className="border-t border-gray-100 px-3 py-1.5 text-[11px] text-gray-400">{footnote}</div>
          )}
        </div>
      )}
    </div>
  );
};
